<template>
  <r-ai-page-container :current-step="7">
    <!-- PR-COACH-V2: surface-and-walk-through Coach. The v1 open-ended
         interview let the conversation chase rabbits and gave the user
         no signal about when STAR was "complete enough." v2 surfaces an
         existing parsed accomplishment from the resume and walks through
         S → T → A → R one element at a time with visible per-element
         progress and a clear stopping rule (approve R = done).

         Four view modes:
           • walkthrough — the per-element capture flow (default)
           • review     — final review of all four STAR elements before
                          save (PR-FIX-COACH-REVIEW); reached after Result
                          is captured, or after a one-shot AI draft
           • capFork    — upgrade-vs-step-by-step at the 3-STAR cap
           • noWork     — empty-state fallback when the resume has no
                          parsed accomplishments to walk through. -->

    <!-- ───────────── Top tracker bar (streamlined) ─────────────
         PR-FIX-COACH-UX: collapsed the old three-column caption grid
         (Wins / Accomplishment / Coaching) into one clean line — win
         progress + role on the left, a single "saved" chip on the right.
         The STAR chips below carry the per-element progress signal. -->
    <div class="tracker-bar pa-3 mb-3">
      <div class="d-flex align-center" style="gap: 10px; min-width: 0;">
        <v-icon color="amber darken-3">mdi-star-four-points</v-icon>
        <div class="flex-grow-1" style="min-width: 0;">
          <div class="text-body-2 font-weight-bold text-truncate">
            <template v-if="currentAccomplishment">
              Win {{ winNumber }} of {{ totalAccomplishments }}
            </template>
            <template v-else>Sage Coach</template>
          </div>
          <div
            v-if="currentAccomplishment"
            class="text-caption text--secondary text-truncate"
            :title="currentRoleLabel"
          >
            {{ currentRoleLabel }}
          </div>
        </div>
        <!-- PR-FIX-COACH-COUNTER: this chip now reflects ACTUAL STAR
             completion (accomplishments with all four elements saved) —
             the same metric AIDone and the Career Profile show — instead
             of the free-cap usage counter, which measured something
             different and read as inconsistent. -->
        <v-chip
          x-small
          label
          outlined
          color="amber darken-3"
          class="flex-shrink-0 font-weight-medium"
        >
          {{ starCompleteCount }} of {{ totalAccomplishments }} done
        </v-chip>
      </div>

      <!-- STAR progress chips. Each fills in as the element is captured.
           Chevron separators reinforce the S→T→A→R direction of travel. -->
      <div
        v-if="currentAccomplishment && viewMode !== 'review'"
        class="d-flex align-center justify-center mt-3 flex-wrap"
        style="gap: 4px;"
      >
        <template v-for="(el, i) in elementOrder">
          <div
            :key="el"
            class="star-chip d-flex align-center"
            :class="{
              'star-chip-done': elementsCaptured[el],
              'star-chip-current': currentElement === el && !elementsCaptured[el],
              'star-chip-pending': currentElement !== el && !elementsCaptured[el]
            }"
          >
            <v-icon size="13" class="mr-1">{{ chipIcon(el) }}</v-icon>
            {{ chipLabel(el) }}
          </div>
          <v-icon
            v-if="i < elementOrder.length - 1"
            :key="el + '-sep'"
            size="14"
            color="grey lighten-1"
            >mdi-chevron-right</v-icon
          >
        </template>
      </div>

      <!-- Review-mode banner: all four parts locked in -->
      <div
        v-else-if="currentAccomplishment && viewMode === 'review'"
        class="d-flex align-center justify-center mt-3 text-caption font-weight-medium review-progress-line"
        style="gap: 6px;"
      >
        <v-icon size="15" color="success">mdi-check-decagram</v-icon>
        All four parts ready — review &amp; save
      </div>
    </div>

    <!-- Mode-switch link. PR-COACH-STAR-UX (item 5): hidden in walkthrough
         mode, where it now lives in the consolidated "More" menu by the input
         so the walkthrough screen has one calm secondary control instead of a
         top link AND a stack of bottom links. Still shown on review / capFork /
         noWork, which have no input-area menu, so the escape is never lost. -->
    <div v-if="viewMode !== 'walkthrough'" class="mb-3 text-center">
      <a
        href="#"
        class="text-caption text--secondary"
        @click.prevent="switchToStepByStep"
      >
        ← Switch to step-by-step
      </a>
    </div>

    <!-- ═════════════════ NO WORK FALLBACK ═════════════════ -->
    <div v-if="viewMode === 'noWork'" class="text-center pa-4">
      <r-ai-bubble>
        <div class="text-body-1 text--primary my-2">
          I don't see any work entries with accomplishments yet. Add a job and a
          bullet first, then come back here to walk it into STAR with me.
        </div>
      </r-ai-bubble>
      <v-btn color="primary" class="mt-3" @click="goAddWork" block>
        Add a job
      </v-btn>
      <v-btn outlined class="mt-2 ai-outlined-btn" @click="saveAndExit" block>
        Save & finish later
      </v-btn>
    </div>

    <!-- ═════════════════ CAP FORK ═════════════════ -->
    <div v-else-if="viewMode === 'capFork'">
      <div class="text-center mb-4 pa-6 milestone-bubble">
        <v-icon size="56" color="amber darken-2" class="mb-3">
          mdi-trophy
        </v-icon>
        <div class="text-h6 font-weight-medium mb-2">
          {{ winsCaptured }} strong wins captured
        </div>
        <div class="text-body-1">
          That's a great foundation. Here's how to keep going:
        </div>
      </div>
      <v-card outlined class="mb-3 pa-4">
        <div class="d-flex align-center mb-2">
          <v-icon color="primary" class="mr-2">mdi-microphone</v-icon>
          <div class="text-body-1 font-weight-medium">
            Keep coaching with Sage
          </div>
        </div>
        <div class="text-body-2 text--secondary mb-3">
          Unlimited STAR walkthrough with Sage. Best if you'd rather talk
          through your wins than type them.
        </div>
        <v-btn color="primary" block @click="upgradeToPlus">
          Unlock with Resumation Plus
        </v-btn>
      </v-card>
      <v-card outlined class="mb-3 pa-4">
        <div class="d-flex align-center mb-2">
          <v-icon color="grey darken-1" class="mr-2">
            mdi-format-list-checks
          </v-icon>
          <div class="text-body-1 font-weight-medium">
            Step-by-step (always free)
          </div>
        </div>
        <div class="text-body-2 text--secondary mb-3">
          Type your own STARs at your own pace. Same end result.
        </div>
        <v-btn
          outlined
          block
          class="ai-outlined-btn"
          @click="switchToStepByStep"
        >
          Continue step-by-step
        </v-btn>
      </v-card>
      <v-btn outlined block class="ai-outlined-btn" @click="saveAndExit">
        Save & finish later
      </v-btn>
    </div>

    <!-- ═════════════════ WALKTHROUGH (per-element) ═════════════════ -->
    <div v-else-if="viewMode === 'walkthrough' && currentAccomplishment">
      <!-- Reference card: original accomplishment from the resume -->
      <v-card outlined class="mb-3 pa-3 accomplishment-ref">
        <div class="d-flex align-start justify-space-between" style="gap: 8px;">
          <div class="text-caption text--secondary mb-1 d-flex align-center flex-wrap" style="gap: 6px;">
            <span>From your resume</span>
            <!-- PR-COACH-CONTEXT: surface role · company · date range so the
                 user knows WHICH job they're STAR'ing for. Bullet alone wasn't
                 enough context — same bullet text could plausibly come from
                 multiple roles, and the user has to mentally re-anchor each
                 time the queue advances. -->
            <span
              v-if="currentAccomplishment.roleContextLine"
              class="role-context-line"
            >· {{ currentAccomplishment.roleContextLine }}</span>
          </div>
          <!-- PR-GEN-STAR: skip the per-element Sage conversation and let the
               AI draft all four STAR slots at once. First click shows the
               accuracy disclosure; after that it generates directly. Visible
               on every accomplishment — using it again is the user's choice. -->
          <v-btn
            x-small
            outlined
            color="#8b5cf6"
            class="flex-shrink-0"
            :loading="aiDrafting"
            :disabled="streaming"
            @click="onGenerateStarClick"
          >
            <v-icon x-small left>mdi-auto-fix</v-icon>
            Generate STAR with AI
          </v-btn>
        </div>
        <div class="text-body-1 text--primary" style="font-style: italic;">
          "{{ currentAccomplishment.originalText }}"
        </div>
      </v-card>

      <!-- Captured-so-far summary, collapsible.
           PR-COACH-STAR-UX (item 2): this used to render the full prose of
           every captured STAR element at once, stacking a wall of text above
           the conversation. The compact STAR tracker pills at the top already
           carry the progress signal, so the full prose only needs to appear
           on demand. Wrapped in a v-expansion-panels collapsed by default
           ("Review captured (N)") — the per-element pencil edit lives inside
           the expander, unchanged. -->
      <v-expansion-panels
        v-if="hasCapturedAnyElement"
        flat
        class="captured-so-far-panels mb-3"
      >
        <v-expansion-panel class="captured-so-far">
          <v-expansion-panel-header class="py-2">
            <div class="d-flex align-center" style="gap: 6px;">
              <v-icon size="16" color="success">mdi-check-circle</v-icon>
              <span class="text-caption font-weight-medium">
                Review captured ({{ capturedElementsInOrder.length }})
              </span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div
              v-for="el in capturedElementsInOrder"
              :key="'cap-' + el"
              class="captured-element-row mb-1"
            >
              <div class="d-flex align-start" style="gap: 8px;">
                <v-icon size="14" color="success" class="mt-1">
                  mdi-check-circle
                </v-icon>
                <div class="flex-grow-1">
                  <div class="text-caption font-weight-medium">
                    {{ chipLabel(el) }}
                  </div>
                  <div class="text-body-2 text--primary">
                    {{ elementsCaptured[el] }}
                  </div>
                </div>
                <v-btn
                  icon
                  x-small
                  @click="editCapturedElement(el)"
                  aria-label="Edit captured element"
                >
                  <v-icon size="14">mdi-pencil</v-icon>
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Sage chat (just the most recent assistant message — we don't
           keep a full chat log here; per-element flow is short by design) -->
      <div v-if="currentAssistantMessage" class="mb-3">
        <r-ai-bubble>
          <div class="d-flex align-start">
            <sage-avatar
              :state="avatarState"
              :size="32"
              class="mr-3"
              style="flex-shrink: 0;"
            />
            <div
              class="text-body-1 text--primary"
              v-html="renderMessage(currentAssistantMessage)"
            ></div>
          </div>
        </r-ai-bubble>
      </div>

      <!-- Streaming-thinking indicator -->
      <div v-if="streaming && !streamingText && !proposedCapture" class="mb-3">
        <r-ai-bubble>
          <div class="d-flex align-center">
            <sage-avatar state="thinking" :size="32" class="mr-3" />
            <div class="thinking-dots d-flex align-center">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="ml-3 text-caption text--secondary">
                {{ streamingStatus || "Thinking…" }}
              </span>
            </div>
          </div>
        </r-ai-bubble>
      </div>

      <!-- Inline streaming text -->
      <div v-if="streamingText && !proposedCapture" class="mb-3">
        <r-ai-bubble>
          <div
            class="text-body-1 text--primary"
            v-html="renderMessage(streamingText)"
          ></div>
        </r-ai-bubble>
      </div>

      <!-- PR-COACH-STAR-UX (item 4): brief tidying state while a manual
           capture is being cleaned server-side before the approval card
           renders, so the user sees progress instead of a frozen button. -->
      <div v-if="cleaningElement && !proposedCapture" class="mb-3">
        <r-ai-bubble>
          <div class="d-flex align-center">
            <sage-avatar state="thinking" :size="32" class="mr-3" />
            <div class="thinking-dots d-flex align-center">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="ml-3 text-caption text--secondary">
                Tidying that up…
              </span>
            </div>
          </div>
        </r-ai-bubble>
      </div>

      <!-- Per-element capture proposal card -->
      <div v-if="proposedCapture" class="mb-3">
        <v-card class="capture-card pa-3">
          <div class="d-flex align-center mb-2">
            <sage-avatar state="presenting" :size="28" class="mr-2" />
            <div class="text-body-2 font-weight-medium flex-grow-1">
              Got it — captured as your
              <span class="text-uppercase">{{ proposedCapture.element }}</span
              >:
            </div>
            <!-- PR-COACH-EDIT: explicit inline-edit affordance. The captured
                 value reads as static text by default; the pencil turns it into
                 an editable field so the user can directly fix Sage's wording
                 (e.g. correct "I don't have any numbers for you" into the real
                 result) without a full re-chat. Their edited words are final —
                 approveElementCapture stores proposedCapture.value as-is. -->
            <v-btn
              v-if="!editingProposal"
              icon
              x-small
              :disabled="committingElement"
              aria-label="Edit captured text"
              @click="startEditProposal"
            >
              <v-icon size="16">mdi-pencil</v-icon>
            </v-btn>
          </div>
          <!-- Editable field (shown after clicking the pencil) -->
          <v-textarea
            v-if="editingProposal"
            ref="proposalEditField"
            v-model="proposedCapture.value"
            auto-grow
            dense
            outlined
            rows="2"
            hide-details
            class="mb-2"
          ></v-textarea>
          <!-- Read view of the captured value -->
          <div
            v-else
            class="proposed-value-readout text-body-2 text--primary mb-2"
          >
            {{ proposedCapture.value }}
          </div>
          <div
            v-if="proposedCapture.hasUnverifiedSpecifics"
            class="warning-banner mb-2"
          >
            <v-icon size="14" color="amber darken-2" class="mr-1">
              mdi-alert-circle-outline
            </v-icon>
            <span class="text-caption">
              Double-check any specifics — they may not match exactly what you
              said.
            </span>
          </div>
          <div class="d-flex" style="gap: 8px;">
            <v-btn
              color="primary"
              small
              class="flex-grow-1"
              :loading="committingElement"
              @click="approveElementCapture"
            >
              <v-icon left small>mdi-check</v-icon>
              Looks right — next
            </v-btn>
            <v-btn
              outlined
              small
              :disabled="committingElement"
              @click="refineElementCapture"
            >
              Refine with more context
            </v-btn>
          </div>
        </v-card>
      </div>

      <!-- Input — only show when there's no pending capture awaiting approval -->
      <div v-if="!proposedCapture && !cleaningElement" class="mt-3">
        <!-- PR-COACH-STAR-UX (item 3): one-tap confirmation chips. When Sage
             reads back what she heard and asks the user to confirm in plain
             text ("Does that sound good?"), the user used to have to TYPE
             "Yes" to move forward. These chips make a confirmation one tap —
             "Yes, capture it" runs the existing capture path (the value is
             cleaned and shown for final approval), "Let me tweak it" focuses
             the input for an actual correction. Matches the LOOKS RIGHT /
             REFINE pattern on the capture card. The free-text input below
             stays available for real edits. -->
        <div
          v-if="sageAwaitingConfirmation"
          class="d-flex flex-wrap mb-3"
          style="gap: 8px;"
        >
          <v-btn color="primary" small @click="quickConfirmCapture">
            <v-icon left small>mdi-check</v-icon>
            Yes, capture it
          </v-btn>
          <v-btn outlined small class="ai-outlined-btn" @click="focusInputForTweak">
            Let me tweak it
          </v-btn>
        </div>

        <voice-input
          v-model="userInput"
          :placeholder="inputPlaceholder"
          :hint="streaming ? 'Sage is responding...' : ''"
          :multiline="true"
          :rows="3"
          :disabled="streaming"
        ></voice-input>

        <!-- Primary action: Send stays prominent and full-width.
             PR-COACH-STAR-UX (item 5): the secondary actions that used to
             compete beside and below Send (Save & finish later, the manual
             capture hatch, Skip) are consolidated into the single tidy row
             below so the screen breathes. No action was removed and none
             changed behavior — only the layout/grouping. -->
        <v-btn
          color="primary"
          :disabled="streaming || !userInput.trim()"
          :loading="streaming"
          block
          class="mt-3"
          @click="sendMessage"
        >
          Send
        </v-btn>

        <!-- Consolidated secondary-action row. The progress-advancing manual
             capture hatch stays a visible quick action (only when there's
             something to capture); everything else lives in a single "More"
             menu so it's one calm control instead of a stack of links. -->
        <div
          v-if="!streaming"
          class="d-flex align-center justify-space-between mt-2"
        >
          <v-btn
            v-if="canManualCapture"
            text
            small
            color="primary"
            @click="manualCaptureElement"
          >
            <v-icon left small>mdi-arrow-right-circle</v-icon>
            That's enough — capture my {{ currentElement }} &amp; move on
          </v-btn>
          <!-- keep the menu pinned right even when the hatch isn't shown -->
          <v-spacer v-else></v-spacer>

          <v-menu offset-y top left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn text small class="text--secondary" v-bind="attrs" v-on="on">
                More
                <v-icon small right>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="saveAndExit">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-content-save</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Save &amp; finish later</v-list-item-title>
              </v-list-item>
              <v-list-item @click="skipAccomplishment">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-skip-next</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Skip this accomplishment</v-list-item-title>
              </v-list-item>
              <v-list-item @click="switchToStepByStep">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-format-list-checks</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Switch to step-by-step</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- ════════════ STAR REVIEW (coached + AI-drafted) ════════════
         PR-FIX-COACH-REVIEW: the final review screen the user reaches after
         all four elements are captured (the coached walk-through) OR after
         a one-shot AI draft. Shows the original bullet + every STAR element,
         all editable, with save / start-over. This is the explicit stopping
         point the per-element flow was missing — before, approving Result
         saved straight to Cosmos with no chance to review the whole thing. -->
    <div v-else-if="viewMode === 'review' && currentAccomplishment">
      <div class="text-h6 font-weight-medium mb-1">Review your win</div>
      <div class="text-body-2 text--secondary mb-3">
        Here's your accomplishment as a complete STAR. Edit anything that's not
        quite right, then save it.
      </div>

      <v-card outlined class="mb-3 pa-3 accomplishment-ref">
        <div
          class="text-caption text--secondary mb-1 d-flex align-center flex-wrap"
          style="gap: 6px;"
        >
          <span>Accomplishment bullet</span>
          <span
            v-if="currentAccomplishment.roleContextLine"
            class="role-context-line"
            >· {{ currentAccomplishment.roleContextLine }}</span
          >
        </div>
        <div class="text-body-1 text--primary" style="font-style: italic;">
          "{{ currentAccomplishment.originalText }}"
        </div>
      </v-card>

      <!-- Strong warning only for AI-drafted STARs; coached ones get a
           gentler nudge since the content is already in the user's words. -->
      <div v-if="aiDraftedStar" class="warning-banner mb-3">
        <v-icon size="16" color="amber darken-2" class="mr-1">
          mdi-alert-circle-outline
        </v-icon>
        <span class="text-caption">
          This is an <strong>AI draft</strong>. Review and edit every field for
          accuracy before saving — the AI inferred details that may not match
          what actually happened.
        </span>
      </div>

      <div v-for="el in elementOrder" :key="'rev-' + el" class="mb-3">
        <div class="text-caption font-weight-medium mb-1 d-flex align-center">
          <v-icon
            size="14"
            :color="elementsCaptured[el] ? 'success' : 'grey'"
            class="mr-1"
          >
            {{ elementsCaptured[el] ? "mdi-check-circle" : "mdi-circle-outline" }}
          </v-icon>
          {{ chipLabel(el) }}
        </div>
        <v-textarea
          v-model="elementsCaptured[el]"
          auto-grow
          dense
          outlined
          rows="2"
          hide-details
          :placeholder="'Add the ' + el + ' here…'"
        ></v-textarea>
      </div>

      <div class="d-flex mt-4" style="gap: 8px;">
        <v-btn
          color="primary"
          class="flex-grow-1"
          :loading="committingElement"
          @click="commitFullStar"
        >
          <v-icon left small>mdi-check</v-icon>
          {{ isLastAccomplishment ? "Save & finish" : "Save & continue" }}
        </v-btn>
        <v-btn
          outlined
          class="ai-outlined-btn"
          :disabled="committingElement"
          @click="startOver"
        >
          <v-icon left small>mdi-restart</v-icon>
          Start over
        </v-btn>
      </div>
      <div class="text-center mt-3">
        <a
          href="#"
          class="text-caption text--secondary"
          @click.prevent="skipAccomplishment"
        >
          Skip this accomplishment →
        </a>
      </div>
    </div>

    <!-- ════════════ AI-DRAFT DISCLOSURE MODAL (PR-GEN-STAR) ════════════ -->
    <v-dialog v-model="showAiDraftDisclosure" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="#8b5cf6">mdi-auto-fix</v-icon>
          Quick heads-up
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-2">
            AI can draft a starting point for all four STAR fields from your
            bullet — it's a head start, not the finished story.
          </p>
          <p class="text-body-2 mb-3">
            It fills the gaps by inferring from your resume, so some details
            may not be exactly right. Give each field a quick read and edit
            before you save — that's the part only you can do.
          </p>
          <v-checkbox
            v-model="aiDraftDisclosureDismissForever"
            label="Got it — don't show this again"
            dense
            hide-details
            class="mt-1"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showAiDraftDisclosure = false">Cancel</v-btn>
          <v-btn color="#8b5cf6" dark @click="confirmDisclosureAndGenerate">
            Draft it
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar feedback -->
    <v-snackbar
      v-model="celebrationVisible"
      color="success"
      timeout="2000"
      bottom
      right
    >
      <v-icon left>mdi-check-decagram</v-icon>
      {{ celebrationMessage }}
    </v-snackbar>
    <v-snackbar v-model="errorVisible" color="error" timeout="5000" bottom>
      {{ errorMessage }}
    </v-snackbar>
  </r-ai-page-container>
</template>

<script>
import VoiceInput from "../../components/VoiceInput.vue";
import SageAvatar from "../../components/SageAvatar.vue";
import {
  getOnboardingStatus,
  approveStar,
  chatStream,
  generateStarDraft,
  acknowledgeAiStarDisclosure,
  cleanStarElement
} from "../../api/SageOnboardingApi";
import { mapGetters, mapState, mapActions, mapMutations } from "vuex";

export default {
  name: "AIWorkAchievementsCoach",
  components: { VoiceInput, SageAvatar },
  data() {
    return {
      // ───── View / cap state ─────
      viewMode: "walkthrough", // "walkthrough" | "review" | "capFork" | "noWork"
      starCap: 3,
      winsCaptured: 0,
      unlocked: false,
      capReached: false,
      isPremium: false,
      companySageUnlocked: false,
      statusLoaded: false,

      // ───── Walkthrough queue ─────
      // accomplishments is the flattened, in-order queue of every parsed
      // achievement across every Work entry. Each item carries enough
      // context that we can navigate independently.
      // Shape: { workKey, originalText, achievementId, roleLabel }
      accomplishments: [],
      currentAccomplishmentIndex: 0,

      // ───── Per-element state ─────
      elementOrder: ["situation", "task", "action", "result"],
      currentElement: "situation",
      // elementsCaptured[el] = captured string (or null/empty if not yet)
      elementsCaptured: {
        situation: null,
        task: null,
        action: null,
        result: null
      },
      sourceNarratives: {
        situation: null,
        task: null,
        action: null,
        result: null
      },
      // PR-FIX-COACH-HISTORY: per-element conversation history. Was sending
      // [] every turn so Sage couldn't synthesize multiple user answers and
      // kept asking about details it had never asked about. Now we maintain
      // a per-element transcript that resets when the user advances elements.
      // Shape: { situation: [{role: 'assistant'|'user', content: '...'}], ... }
      elementHistory: {
        situation: [],
        task: [],
        action: [],
        result: []
      },
      // PR-GEN-STAR: Generate-STAR-with-AI state
      showAiDraftDisclosure: false,
      aiDraftDisclosureAcknowledged: false,
      aiDraftDisclosureDismissForever: false,
      aiDrafting: false,
      // True once the current accomplishment's STAR was AI-drafted — carried
      // into the save payload as source="ai_drafted" for the audit trail.
      aiDraftedStar: false,

      // ───── Chat state ─────
      currentAssistantMessage: "",
      userInput: "",
      streaming: false,
      streamingText: "",
      streamingStatus: "",
      conversationId: null,

      // ───── Pending capture (per-element approval card) ─────
      proposedCapture: null,
      // PR-COACH-EDIT: when true, the proposal card shows the captured value
      // as an editable textarea instead of static text. Reset to false every
      // time a fresh proposal appears so each new capture starts read-only.
      editingProposal: false,
      committingElement: false,
      // PR-COACH-STAR-UX (item 4): true while a manual capture is being
      // cleaned server-side (transcription fix / filler removal) before the
      // approval card renders. Drives a brief "Tidying that up…" state.
      cleaningElement: false,

      // ───── UI feedback ─────
      celebrationVisible: false,
      celebrationMessage: "",
      errorVisible: false,
      errorMessage: ""
    };
  },
  computed: {
    ...mapState({
      workDict: state => state.work && state.work.workDict,
      // PR-COACH-POINTER: where the user left off, persisted via
      // vuex-persistedstate (sessionStorage). Null when fresh session OR
      // when flow was completed (AIDone clears it).
      coachPointer: state => state.work && state.work.coachPointer
    }),
    ...mapGetters("work", ["fullList"]),

    totalAccomplishments() {
      return this.accomplishments.length;
    },
    // PR-FIX-COACH-COUNTER: count of accomplishments whose four STAR
    // elements are all captured. Derived from the queue's per-item
    // `isComplete` flag, which is seeded from saved BRAM fields at build
    // time and updated live by commitFullStar — so the tracker chip stays
    // tied to actual results and consistent with AIDone / Career Profile.
    starCompleteCount() {
      return this.accomplishments.filter(a => a && a.isComplete).length;
    },
    // PR-COACH-RESUME-FIX: the "Win N of M" the user is working on = wins
    // already captured + 1, capped at the total. Decoupled from the queue
    // index, which shifts when the queue is rebuilt and previously made the
    // header read "Win 6 of 6" right after the 3rd save.
    winNumber() {
      var n = this.starCompleteCount + 1;
      var total = this.totalAccomplishments || n;
      return n > total ? total : n;
    },
    currentAccomplishment() {
      if (
        this.accomplishments.length === 0 ||
        this.currentAccomplishmentIndex >= this.accomplishments.length
      ) {
        return null;
      }
      return this.accomplishments[this.currentAccomplishmentIndex];
    },
    currentRoleLabel() {
      return this.currentAccomplishment
        ? this.currentAccomplishment.roleLabel
        : "your current role";
    },
    hasCapturedAnyElement() {
      return this.elementOrder.some(el => !!this.elementsCaptured[el]);
    },
    // PR-COACH-V2: only the captured elements, in S→T→A→R order. Used
    // by the captured-so-far summary block. Avoids the v-if-with-v-for
    // anti-pattern.
    capturedElementsInOrder() {
      const self = this;
      return this.elementOrder.filter(el => !!self.elementsCaptured[el]);
    },
    inputPlaceholder() {
      if (this.currentElement === "situation")
        return "Tell me about the situation… (tap mic or type)";
      if (this.currentElement === "task")
        return "What were YOU specifically responsible for?";
      if (this.currentElement === "action")
        return "What did you specifically do?";
      if (this.currentElement === "result")
        return "What changed because of what you did?";
      return "Continue the conversation…";
    },
    avatarState() {
      if (this.streaming) return "thinking";
      if (this.proposedCapture) return "presenting";
      return "idle";
    },
    // PR-FIX-COACH-REVIEW: true when the current accomplishment is the last
    // one in the queue — drives the "Save & finish" vs "Save & continue"
    // label on the review screen so the user knows whether saving advances
    // to the next win or wraps up the whole walk-through.
    isLastAccomplishment() {
      return (
        this.accomplishments.length > 0 &&
        this.currentAccomplishmentIndex >= this.accomplishments.length - 1
      );
    },
    // PR-FIX-COACH-PROGRESS: true once the user has given Sage anything for
    // the current element — either a sent turn in the per-element transcript
    // or text sitting in the input box. Gates the "capture & move on" escape
    // hatch so the user can always advance even if Sage keeps asking
    // follow-ups instead of calling capture_element.
    canManualCapture() {
      if (!this.currentAccomplishment) return false;
      if (this.proposedCapture) return false;
      const hist = this.elementHistory[this.currentElement] || [];
      const hasTurn = hist.some(t => t.role === "user");
      return hasTurn || !!(this.userInput && this.userInput.trim());
    },
    // PR-COACH-STAR-UX (item 3): true when Sage has replied with a plain-text
    // confirmation question (e.g. "Does that sound good to you?") instead of
    // firing the capture_element tool — the user otherwise has to TYPE "Yes"
    // to move forward. We detect a confirmation-shaped prompt so we can offer
    // one-tap chips. Gated to the case where there IS something to capture
    // (the user has already shared an answer this element) and no capture card
    // is currently up. Heuristic only — false negatives just fall back to the
    // existing free-text flow, which still works.
    sageAwaitingConfirmation() {
      if (this.proposedCapture || this.streaming) return false;
      if (!this.canManualCapture) return false;
      const msg = (this.currentAssistantMessage || "").toLowerCase();
      if (!msg) return false;
      // Confirmation-style phrasings Sage uses when she's read back what she
      // heard and is asking the user to bless it before capturing.
      return (
        msg.includes("sound good") ||
        msg.includes("sounds good") ||
        msg.includes("does that") ||
        msg.includes("did i get") ||
        msg.includes("is that right") ||
        msg.includes("look right") ||
        msg.includes("looks right") ||
        msg.includes("capture that") ||
        msg.includes("capture this") ||
        msg.includes("ready to capture") ||
        msg.includes("shall i capture") ||
        msg.includes("want me to capture")
      );
    }
  },
  watch: {
    // PR-FIX-COACH-LOAD: reactive backstop. If the work store loads after
    // the initial mount (e.g. the user landed here before the parser
    // finished its Cosmos save, or a refresh raced with the store dispatch),
    // rebuild the queue and re-evaluate the view mode. Only fires when the
    // user is currently parked on the noWork view, so we never disrupt a
    // user mid-conversation with a queue rebuild they didn't ask for.
    fullList: {
      handler() {
        if (
          this.viewMode === "noWork" &&
          this.fullList &&
          this.fullList.length > 0
        ) {
          this.buildAccomplishmentQueue();
          if (this.accomplishments.length > 0) {
            this.viewMode = "walkthrough";
            this.startCurrentAccomplishment();
          }
        }
      },
      deep: true
    },
    // PR-COACH-POINTER: persist the user's position whenever they advance
    // through the queue. Belt-and-suspenders so a tab close, browser crash,
    // or sudden route change (e.g., user clicks back to Career Profile mid-
    // walkthrough) preserves the resume point without relying on every exit
    // path to remember to call savePointerFromCurrent. setCoachPointer is
    // a synchronous mutation; calling it on every queue advance is cheap.
    currentAccomplishmentIndex(newVal) {
      if (
        this.viewMode === "walkthrough" &&
        typeof newVal === "number" &&
        newVal >= 0
      ) {
        this.savePointerFromCurrent();
      }
    }
  },
  async mounted() {
    // PR-FIX-STRIPE-RETURN: detect the post-upgrade return. ResumeLanding
    // redirects here with ?upgraded=1 when paymentReturn fired. Show a
    // welcome-back acknowledgement so the user knows the upgrade went
    // through, then strip the query param so a refresh doesn't re-fire it.
    var returnedUpgraded = false;
    if (this.$route.query && this.$route.query.upgraded === "1") {
      returnedUpgraded = true;
      // PR-COACH-RESUME-FIX-2: the user just unlocked Resumation Plus, so the
      // free STAR cap no longer applies. Clear the cap-fork state up front so
      // the mount sequence below routes them straight into the walkthrough at
      // the first incomplete win — NOT back into the cap-fork on the win that
      // tripped the cap. refreshStatus() re-reads the backend (which now
      // reports unlimited=true / capReached=false for a premium user), but we
      // clear here too so there's no window where stale cap state diverts the
      // view-mode decision.
      this.capReached = false;
      this.unlocked = true;
      this.celebrationMessage =
        "Welcome back — your Resumation Plus is active. Sage is ready to keep going.";
      this.celebrationVisible = true;
      try {
        var cleanQuery = { ...this.$route.query };
        delete cleanQuery.upgraded;
        this.$router.replace({ query: cleanQuery });
      } catch (e) {
        /* router quirk — non-fatal */
      }
    }

    // PR-FIX-COACH-LOAD: race condition. Carlos hit the Coach screen and
    // saw "I don't see any work entries with accomplishments yet" even
    // though his uploaded resume had 9 jobs with bullets. Root cause:
    // buildAccomplishmentQueue() runs synchronously and captures whatever
    // fullList contains AT MOUNT TIME. If the user navigated here before
    // the work store finished loading (or directly via URL, or after a
    // browser refresh that bypassed onboarding flow's loadFullResume
    // dispatch), fullList is empty -> queue is empty -> noWork view fires.
    //
    // Fix: dispatch loadWork explicitly and AWAIT it before building the
    // queue. Belt-and-suspenders: the watcher below rebuilds the queue
    // any time fullList changes thereafter, so any other late-arriving
    // work payload also surfaces.
    try {
      await this.loadWork();
    } catch (e) {
      /* loadWork can fail when there's no resume — buildAccomplishmentQueue
         handles empty fullList gracefully, so we just continue */
    }

    // 1. Pull the flattened accomplishment queue from the parsed resume.
    this.buildAccomplishmentQueue();

    // 2. Resolve entitlement / cap state from backend.
    await this.refreshStatus();

    // 3. PR-COACH-POINTER: if we have a saved pointer (user left mid-flow,
    //    or returned from Stripe with ?upgraded=1), seek the queue to that
    //    accomplishment. Stale-pointer fallback: if the workKey or
    //    achievementId no longer exists in the queue (e.g., user edited
    //    their resume between sessions), we silently fall through to
    //    "start from the beginning" — better than crashing or landing on
    //    the wrong slot. Only applies in walkthrough mode; cap-fork
    //    overrides because the user needs to make a fork choice first.
    if (this.coachPointer && this.accomplishments.length > 0 && !returnedUpgraded) {
      var seekKey = this.coachPointer.workKey;
      var seekId = this.coachPointer.achievementId;
      var pointerIdx = this.accomplishments.findIndex(function(a) {
        return a && a.workKey === seekKey && a.achievementId === seekId;
      });
      // PR-COACH-RESUME-FIX: only honor the pointer if it targets an
      // accomplishment that still needs work. A pointer to an already-complete
      // STAR (the one that tripped the free cap, or a stale pointer from a
      // prior session) would otherwise land the user on a finished win — fall
      // through to the firstIncomplete index buildAccomplishmentQueue set.
      if (
        pointerIdx >= 0 &&
        this.accomplishments[pointerIdx] &&
        !this.accomplishments[pointerIdx].isComplete
      ) {
        this.currentAccomplishmentIndex = pointerIdx;
      }
    }
    // PR-COACH-RESUME-FIX-2: on a confirmed post-Stripe upgrade return, ignore
    // the saved pointer entirely (it points at the cap-fork win the user just
    // finished, or was mid-way on) and always land on the GLOBAL first
    // incomplete win — the same notion of "incomplete" the queue and step-by-
    // step share (all 4 BRAM fields). buildAccomplishmentQueue already set
    // currentAccomplishmentIndex to firstIncomplete; we recompute defensively
    // in case the pointer block above (now skipped) or a queue rebuild moved it.
    if (returnedUpgraded) {
      var firstIncompleteIdx = this.accomplishments.findIndex(function(a) {
        return a && !a.isComplete;
      });
      this.currentAccomplishmentIndex =
        firstIncompleteIdx >= 0 ? firstIncompleteIdx : 0;
    }

    // 4. Pick the right initial view mode.
    if (this.accomplishments.length === 0) {
      this.viewMode = "noWork";
      return;
    }
    // PR-COACH-RESUME-FIX-2: a confirmed post-Stripe upgrade return ALWAYS goes
    // to the walkthrough — the user paid to keep coaching, so re-showing the
    // cap-fork would be a dead end. We guard on returnedUpgraded (not just the
    // cleared capReached) so even a stale/racing backend read of capReached=true
    // can't divert them back into the fork.
    if (this.capReached && !returnedUpgraded) {
      this.viewMode = "capFork";
      return;
    }

    // 5. Start the seek'd-to accomplishment (or first if no pointer).
    this.viewMode = "walkthrough";
    this.startCurrentAccomplishment();
  },
  methods: {
    ...mapActions("work", ["loadWork"]),
    ...mapMutations("work", ["setCoachPointer"]),

    // PR-COACH-POINTER: persist the user's position to Vuex (which is
    // sessionStorage-backed via vuex-persistedstate). Called from any exit
    // path AND continuously as the user moves through the queue so an
    // interruption (browser close, Stripe roundtrip, route change) preserves
    // position. No-op if there's no current accomplishment.
    savePointerFromCurrent() {
      var accomp = this.currentAccomplishment;
      if (!accomp || !accomp.workKey || !accomp.achievementId) return;
      this.setCoachPointer({
        workKey: accomp.workKey,
        achievementId: accomp.achievementId,
      });
    },

    // ═══════════════ Queue + state machine ═══════════════

    buildAccomplishmentQueue() {
      // Walks the user's Work entries in resume order and emits one item
      // per existing achievement bullet. If a work entry has no bullets,
      // we skip it — there's nothing to walk through. Users add bullets
      // either before this flow (during the existing AIWorkAchievements
      // review) or via the "Add a job" escape hatch.
      //
      // PR-FIX-COACH-RESUME: each item now also carries `elementsAlreadyCaptured`
      // (per-element BRAM values already on the WorkAchievement) +
      // `completeCount` + `isComplete`. The queue is reordered so
      // partially-progressing accomplishments come first, then bullet-only
      // (un-STAR'd) ones, then fully-complete ones at the end. That way a
      // returning user lands on the most relevant accomplishment instead
      // of restarting from the top of their resume every session.
      const raw = [];
      const list = this.fullList || [];
      list.forEach(entry => {
        // PR-FIX-COACH-QUEUE: prefer `achievementList` (raw WorkAchievement
        // objects with BRAM + displayCustom/displayAuto + id) over
        // `achievements` which is a display-string array produced by
        // buildAchievements() — strings have no .displayCustom etc, so the
        // queue silently filtered every parsed-resume achievement out.
        const ach = entry.achievementList || entry.achievements || [];
        const roleLabel = this.buildRoleLabel(entry);
        ach.forEach(a => {
          // Defensive: if a string slipped through (legacy callers), wrap.
          if (typeof a === "string") {
            a = { displayCustom: a };
          }
          const original =
            a.displayCustom || a.displayAuto || a.background || a.text || "";
          if (!original.trim()) return;

          // Pull existing BRAM values off the saved achievement so the
          // walkthrough can resume at the first gap and pre-render what's
          // already locked in.
          const elementsAlreadyCaptured = {
            situation: (a.background || "").trim() || null,
            task: (a.responsibility || "").trim() || null,
            action: (a.actionResult || "").trim() || null,
            result: (a.measurement || "").trim() || null
          };
          const completeCount = [
            "situation",
            "task",
            "action",
            "result"
          ].filter(el => !!elementsAlreadyCaptured[el]).length;

          raw.push({
            workKey: entry.key,
            achievementId: a.id,
            originalText: original,
            roleLabel,
            // PR-COACH-CONTEXT: per-accomplishment role context line. Surfaces
            // the role + company + date range above the bullet so the user
            // knows WHICH job they're STAR'ing for, not just what the bullet
            // said. Kept as a single tasteful subtitle line — not a heavy
            // header — to add context without clutter.
            roleContextLine: this.buildRoleContextLine(entry),
            elementsAlreadyCaptured,
            completeCount,
            isComplete: completeCount === 4
          });
        });
      });

      // PR-COACH-RESUME-FIX: keep the queue in STABLE resume order. The old
      // code reordered (partial, then bullets, then complete) on every
      // rebuild, which shifted positions out from under the index-based
      // navigation — so after saving a STAR the header showed the wrong
      // "Win N", and resume/upgrade landed on a finished win. "Land on the
      // first incomplete" is preserved below via firstIncomplete; nothing now
      // depends on a moving queue order.
      this.accomplishments = raw;

      // Land the user on the first NOT-yet-complete accomplishment.
      // If everything's already complete (returning user who finished),
      // we'd render the noWork-like "you're all caught up" but for now
      // we just park at index 0 of the complete list — they can click
      // back to the celebration page via Save & finish later.
      const firstIncomplete = this.accomplishments.findIndex(
        q => !q.isComplete
      );
      this.currentAccomplishmentIndex =
        firstIncomplete >= 0 ? firstIncomplete : 0;
    },
    // PR-FIX-COACH-CONTEXT-2: switched to reading the display-string fields
    // (entry.general / entry.roles / entry.date) that the work_store
    // fullList getter actually exposes. Previous code read entry.workGeneral
    // and entry.workRole which DON'T EXIST on fullList items (they're
    // internal to the workDict and stripped by the getter into
    // pre-rendered display strings). Effect: role context line was always
    // empty -> the v-if hid it, even when the parser had populated
    // companyName + position title + dates correctly.
    buildRoleLabel(entry) {
      if (!entry) return "this role";
      const company = entry.general || "";
      const roles = Array.isArray(entry.roles) ? entry.roles : [];
      const title = (roles[0] || "").trim();
      if (title && company) return `${title} @ ${company}`;
      return title || company || "this role";
    },
    // PR-COACH-CONTEXT: builds the subtitle line shown above the bullet
    // in the walkthrough view. Format: "Title · Company · MMM YYYY – MMM YYYY".
    // Falls back gracefully when fields are missing (parser may not have
    // populated dates for every work entry).
    buildRoleContextLine(entry) {
      if (!entry) return "";
      // fullList exposes pre-rendered strings, not the raw objects:
      //   entry.general    = company name (e.g. "Amazon")
      //   entry.roles      = array of role-display strings (e.g. ["Senior PM"])
      //   entry.date       = formatted range (e.g. "Jan 2020 - Present")
      const company = (entry.general || "").trim();
      const roles = Array.isArray(entry.roles) ? entry.roles : [];
      const title = (roles[0] || "").trim();
      const dateRange = (entry.date || "").trim();
      var parts = [];
      if (title) parts.push(title);
      if (company) parts.push(company);
      if (dateRange) parts.push(dateRange);
      return parts.join(" · ");
    },
    formatRoleDateRange(startDate, endDate, workingCurrently) {
      // Dates may arrive as ISO strings, "YYYY-MM" strings, or Date objects.
      // We render a tasteful "MMM YYYY" format; falls back to the raw value
      // when parsing fails so we never show a "NaN NaN" string.
      var fmt = function(d) {
        if (!d) return "";
        try {
          var dt = d instanceof Date ? d : new Date(d);
          if (isNaN(dt.getTime())) return ("" + d).slice(0, 7); // raw "YYYY-MM" fallback
          var months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
          ];
          return months[dt.getMonth()] + " " + dt.getFullYear();
        } catch (e) {
          return "" + d;
        }
      };
      var start = fmt(startDate);
      var end = workingCurrently ? "Present" : fmt(endDate);
      if (start && end) return start + " – " + end;
      if (start) return start + " – Present";
      if (end) return "ended " + end;
      return "";
    },
    startCurrentAccomplishment() {
      // PR-FIX-COACH-RESUME: pre-populate elementsCaptured from any BRAM
      // fields already on the saved WorkAchievement, so a returning user
      // sees what they captured last session and resumes at the first gap
      // instead of restarting from Situation. The source-narrative trail
      // doesn't persist to Cosmos (it's transient context for the LLM),
      // so we don't have it on resume — that's fine; the captured value
      // is what matters for STAR-completeness.
      const accomp = this.currentAccomplishment;
      if (!accomp) return;
      // PR-FIX-COACH-REVIEW: when advancing here from a just-saved review
      // screen, viewMode is still "review" — reset it so the next
      // accomplishment opens in the walk-through, not a stale review card.
      this.viewMode = "walkthrough";
      const saved = accomp.elementsAlreadyCaptured || {
        situation: null,
        task: null,
        action: null,
        result: null
      };
      this.elementsCaptured = {
        situation: saved.situation || null,
        task: saved.task || null,
        action: saved.action || null,
        result: saved.result || null
      };
      this.sourceNarratives = {
        situation: null,
        task: null,
        action: null,
        result: null
      };

      // Find the first STAR element that isn't yet captured — that's where
      // the walkthrough resumes. If everything's captured (shouldn't happen
      // since the queue puts complete accomplishments last, but defensive),
      // land on Situation.
      const firstGap = this.elementOrder.find(el => !this.elementsCaptured[el]);
      this.currentElement = firstGap || "situation";

      this.proposedCapture = null;
      this.userInput = "";
      this.streaming = false;
      this.streamingText = "";
      this.streamingStatus = "";
      this.conversationId = null; // fresh conversation per accomplishment
      // PR-FIX-COACH-HISTORY: reset the per-element transcript for the new
      // accomplishment so Sage doesn't carry the prior bullet's context.
      this.elementHistory = { situation: [], task: [], action: [], result: [] };
      // PR-GEN-STAR: each accomplishment starts un-AI-drafted.
      this.aiDraftedStar = false;

      // Greeting changes shape based on whether we're resuming or starting
      // fresh. Resumption uses a "welcome back, here's what we already
      // have" frame so the user knows their prior work is intact.
      const capturedList = this.elementOrder.filter(
        el => !!this.elementsCaptured[el]
      );
      if (capturedList.length > 0) {
        const formatted = this.formatElementList(capturedList);
        const elementLabel = this.chipLabel(this.currentElement);
        this.currentAssistantMessage =
          "Welcome back. I have your " +
          formatted +
          " for this accomplishment already — let's pick up with the **" +
          elementLabel +
          "**. " +
          this.elementPromptShort(this.currentElement);
      } else {
        this.currentAssistantMessage = this.greetingForElement(
          this.currentElement
        );
      }
    },
    // PR-FIX-COACH-RESUME: friendly comma list for the resume greeting.
    // "Situation" → "Situation"; "Situation, Task" → "Situation and Task";
    // "Situation, Task, Action" → "Situation, Task, and Action".
    formatElementList(elements) {
      const labels = elements.map(el => this.chipLabel(el));
      if (labels.length === 1) return labels[0];
      if (labels.length === 2) return labels[0] + " and " + labels[1];
      return (
        labels.slice(0, -1).join(", ") + ", and " + labels[labels.length - 1]
      );
    },
    // PR-FIX-COACH-RESUME: short, conversational version of the per-element
    // ask. Used in the resumption greeting where we don't want to repeat
    // the full first-time framing.
    elementPromptShort(el) {
      switch (el) {
        case "situation":
          return "What was the context that made this work necessary?";
        case "task":
          return "What were YOU specifically responsible for?";
        case "action":
          return "What did you specifically do?";
        case "result":
          return "What changed because of what you did?";
        default:
          return "";
      }
    },

    advanceElement() {
      const idx = this.elementOrder.indexOf(this.currentElement);
      if (idx < 0 || idx >= this.elementOrder.length - 1) {
        // We were on the last element (Result). PR-FIX-COACH-REVIEW: go to
        // the review screen — NOT a straight Cosmos write. The user gets to
        // see the whole STAR, edit any element, and explicitly save.
        this.goToReview();
        return;
      }
      this.currentElement = this.elementOrder[idx + 1];
      this.userInput = "";
      this.proposedCapture = null;
      this.conversationId = null; // fresh conversation per element

      // Seed the next element's greeting client-side.
      this.currentAssistantMessage = this.greetingForElement(
        this.currentElement
      );
    },

    // PR-FIX-COACH-COHESION: greetings now bridge from the prior element so
    // the four steps feel like one continuous story instead of four cold
    // restarts. Each one acknowledges progress before asking the next thing.
    greetingForElement(el) {
      switch (el) {
        case "situation":
          return "Let's build this one out together, one piece at a time. Start with the **Situation** — looking at the bullet above, what was going on? When, where, and what made this work necessary?";
        case "task":
          return "Got your Situation. Now the **Task** — what were YOU specifically on the hook for here? Not the whole team's job, just your piece of it.";
        case "action":
          return "Nice — that's the setup. Now the **Action**, the heart of the story. Walk me through what YOU actually did: the decisions you made, the things you built, the calls you owned.";
        case "result":
          return "Last piece — the **Result**. What changed because of what you did? Numbers are great if you've got them, even rough ones. Qualitative impact counts too.";
        default:
          return "";
      }
    },

    // ═══════════════ Send a turn to Sage ═══════════════

    async sendMessage() {
      if (!this.userInput.trim() || this.streaming) return;
      if (!this.currentAccomplishment) return;

      const userText = this.userInput.trim();
      this.userInput = "";
      this.streaming = true;
      this.streamingText = "";
      this.streamingStatus = "Thinking…";
      this.currentAssistantMessage = "";

      // PR-FIX-COACH-HISTORY: append user turn to the per-element transcript
      // BEFORE building the request, so the LLM sees its own prior question
      // + every user answer this element has accumulated. Without this Sage
      // was stateless per turn and would drill on fabricated specifics it
      // never asked about, or repeat questions with no awareness of prior
      // answers. The accumulated history is what the LLM synthesizes into
      // the captured STAR value when it finally fires capture_element.
      const currentHistory = this.elementHistory[this.currentElement] || [];
      currentHistory.push({ role: "user", content: userText });

      // Build the per-element request payload.
      const req = {
        message: userText,
        workKey: this.currentAccomplishment.workKey,
        conversationId: this.conversationId,
        // Per-element context — this is the v2 contract.
        currentAccomplishment: this.currentAccomplishment.originalText,
        currentElement: this.currentElement,
        elementsSoFar: {
          situation: this.elementsCaptured.situation || "",
          task: this.elementsCaptured.task || "",
          action: this.elementsCaptured.action || "",
          result: this.elementsCaptured.result || ""
        },
        // PR-FIX-COACH-HISTORY: send the full per-element transcript so Sage
        // can synthesize across all user answers when capturing. Excludes the
        // turn we just pushed (the backend's `message` field is that turn).
        conversationHistory: currentHistory
          .slice(0, -1)
          .map(t => ({ role: t.role, content: t.content }))
      };

      const self = this;
      // PR-FIX-COACH-PROGRESS: chatStream fires onDone twice — once for the
      // "done" SSE event and once in its finally block. Guard so we don't
      // double-append the transcript stub or double-process the turn.
      let doneHandled = false;
      await chatStream(req, {
        onStart(ev) {
          if (ev && ev.conversationId) self.conversationId = ev.conversationId;
        },
        onThinking() {
          self.streamingStatus = "Thinking…";
        },
        onToolCall(ev) {
          if (ev.tool === "capture_element") {
            self.streamingStatus =
              "Capturing your " + self.currentElement + "…";
          } else if (ev.tool === "propose_star_draft") {
            self.streamingStatus = "Drafting your STAR…";
          }
        },
        onText(ev) {
          self.streamingText += ev.content || "";
        },
        onElementCapture(ev) {
          if (ev && ev.elementCapture) {
            // Pull the capture into local state so the approval card renders.
            self.proposedCapture = JSON.parse(
              JSON.stringify(ev.elementCapture)
            );
            self.streamingText = "";
            self.streamingStatus = "";
          }
        },
        onCapState(ev) {
          if (ev && ev.capState) {
            self.winsCaptured = ev.capState.starsApproved;
            self.capReached = ev.capState.capReached;
            self.unlocked = ev.capState.unlimited;
          }
        },
        onError(msg) {
          self.errorMessage = msg || "Coach is having trouble right now.";
          self.errorVisible = true;
        },
        onDone() {
          if (doneHandled) return;
          doneHandled = true;
          // If we got a capture, the approval card is showing — the streamed
          // text is just an intro line we can use as the assistant message.
          // If we got NO capture, the streamed text is Sage asking a
          // clarifying question — show that as the assistant message.
          if (!self.proposedCapture && self.streamingText) {
            self.currentAssistantMessage = self.streamingText;
            // PR-FIX-COACH-HISTORY: also commit Sage's reply to the
            // per-element transcript so the next user turn sees it.
            const h = self.elementHistory[self.currentElement] || [];
            h.push({ role: "assistant", content: self.streamingText });
          } else if (self.proposedCapture) {
            // Capture fired — record a brief stub so the next element's
            // transcript starts clean. Actual capture value is on elementsCaptured.
            const h = self.elementHistory[self.currentElement] || [];
            h.push({
              role: "assistant",
              content: "[captured " + self.currentElement + "]"
            });
          }
          self.streamingText = "";
          self.streamingStatus = "";
          self.streaming = false;
        }
      });
    },

    // ═══════════════ Per-element approval ═══════════════

    // PR-COACH-EDIT: flip the proposal card into edit mode and focus the
    // field so the user can directly correct Sage's captured wording. Their
    // edited text in proposedCapture.value is what approveElementCapture
    // stores — the user's words are final.
    startEditProposal() {
      if (!this.proposedCapture || this.committingElement) return;
      this.editingProposal = true;
      this.$nextTick(() => {
        try {
          const field = this.$refs.proposalEditField;
          if (field && typeof field.focus === "function") field.focus();
        } catch (e) {
          /* focus is best-effort */
        }
      });
    },

    approveElementCapture() {
      if (!this.proposedCapture) return;
      const el = this.proposedCapture.element;
      const value = this.proposedCapture.value;
      const source = this.proposedCapture.sourceNarrative;

      // Commit locally. NOT a Cosmos write — that happens once all four
      // elements are approved and we call ApproveStar. PR-COACH-EDIT: value
      // is whatever sits in proposedCapture.value now, including any direct
      // inline edits the user made via the pencil — their words are final.
      this.elementsCaptured[el] = value;
      this.sourceNarratives[el] = source;
      this.editingProposal = false;
      this.proposedCapture = null;

      // If we just approved Result, head to the review screen. Otherwise
      // advance to the next element. PR-FIX-COACH-REVIEW: the review screen
      // is the explicit stopping point — no silent save on Result approval.
      if (el === "result") {
        this.goToReview();
      } else {
        this.advanceElement();
      }
    },

    refineElementCapture() {
      // User wants Sage to ask for more context. Discard the proposal
      // and let the user say more.
      this.proposedCapture = null;
      this.currentAssistantMessage =
        "Got it — give me a little more on this. What other context, detail, or specific would help capture it accurately?";
    },

    editCapturedElement(el) {
      // User clicked the pencil on an already-captured element. Move
      // back to that element so they can resay it. The other captured
      // elements stay; only the one they clicked is rolled back.
      this.elementsCaptured[el] = null;
      this.sourceNarratives[el] = null;
      this.currentElement = el;
      this.proposedCapture = null;
      this.userInput = "";
      this.currentAssistantMessage = this.greetingForElement(el);
    },

    skipAccomplishment() {
      // Move past this one without saving. Mostly used when an existing
      // bullet just isn't worth STAR-ing.
      this.advanceAccomplishment();
    },

    // ═══════════════ Review screen (PR-FIX-COACH-REVIEW) ═══════════════

    // Lands the user on the final review screen. Reached after Result is
    // captured (coached flow) or after a one-shot AI draft. Clears any
    // transient chat/streaming state so the review screen is clean.
    goToReview() {
      this.proposedCapture = null;
      this.streaming = false;
      this.streamingText = "";
      this.streamingStatus = "";
      this.currentAssistantMessage = "";
      this.userInput = "";
      this.viewMode = "review";
    },

    // PR-COACH-STAR-UX (item 3): one-tap "Yes, capture it" on Sage's plain-text
    // confirmation. Routes through the same capture path the user would reach
    // by clicking "That's enough — capture & move on", so the value is cleaned
    // and shown for final approval rather than committed blind.
    quickConfirmCapture() {
      if (this.streaming || this.proposedCapture) return;
      this.manualCaptureElement();
    },

    // PR-COACH-STAR-UX (item 3): "Let me tweak it" — keep the free-text input
    // for an actual correction and put the cursor in it. Best-effort focus:
    // VoiceInput wraps the textarea, so we reach into its DOM. If the query
    // misses (component refactor, SSR), the input is still right there for the
    // user to click — focus is a nicety, not load-bearing.
    focusInputForTweak() {
      this.proposedCapture = null;
      this.$nextTick(() => {
        try {
          const root = this.$el;
          const ta = root && root.querySelector(".voice-textarea textarea");
          if (ta) ta.focus();
        } catch (e) {
          /* focus is best-effort */
        }
      });
    },

    // PR-FIX-COACH-PROGRESS: deterministic, client-side capture. Builds a
    // proposedCapture for the current element out of everything the user has
    // already said — every sent turn in the per-element transcript plus any
    // text still in the input box. Guarantees the user can always advance,
    // even if Sage never calls capture_element herself. The user still
    // reviews + edits the value in the capture card before approving.
    //
    // PR-COACH-STAR-UX (item 4): the raw join of voice transcript turns used to
    // be shown to the user verbatim — filler words, transcription errors, and
    // all — so the Action/Result (which the user most often reaches via this
    // escape hatch) read as a raw transcript while the Situation/Task that went
    // through Sage's capture_element tool came back as clean prose. We now send
    // the raw join through a server-side clean-up pass (CleanStarElement) that
    // fixes transcription errors, removes filler, and fixes grammar WITHOUT
    // summarizing or genericizing or inventing — the same detail-faithful rule
    // as the Sage capture prompt. The cleaned text is then shown in the same
    // "looks right?" approval card every element uses, so the user never
    // confirms a raw transcript. Graceful fallback: if the cleanup call fails,
    // we fall back to the raw join so the user is never blocked.
    async manualCaptureElement() {
      if (this.streaming || this.proposedCapture) return;
      const el = this.currentElement;
      const hist = this.elementHistory[el] || [];
      const parts = hist
        .filter(t => t.role === "user")
        .map(t => (t.content || "").trim())
        .filter(Boolean);
      const typed = (this.userInput || "").trim();
      if (typed) parts.push(typed);
      const rawValue = parts.join(" ").trim();
      if (!rawValue) return;
      this.userInput = "";
      this.currentAssistantMessage = "";

      // Show a brief working state on the capture card while we clean.
      this.cleaningElement = true;
      let cleaned = rawValue;
      try {
        const resp = await cleanStarElement({
          element: el,
          rawText: rawValue,
          accomplishment: this.currentAccomplishment
            ? this.currentAccomplishment.originalText
            : ""
        });
        if (resp && resp.success && resp.data && resp.data.cleaned) {
          cleaned = resp.data.cleaned;
        }
      } catch (e) {
        /* fall back to raw — user is never blocked */
      } finally {
        this.cleaningElement = false;
      }

      this.proposedCapture = {
        element: el,
        value: cleaned,
        // Keep the user's actual words as the source narrative so the
        // hallucination-highlight + audit trail reflect what they really said.
        sourceNarrative: rawValue,
        hasUnverifiedSpecifics: false
      };
    },

    // Review-screen "Start over": wipe every captured element + the
    // conversation transcript and drop back to Situation. Used when the
    // user wants to redo the whole STAR from scratch (covers both the
    // coached path and discarding an AI draft).
    startOver() {
      this.elementsCaptured = {
        situation: null,
        task: null,
        action: null,
        result: null
      };
      this.sourceNarratives = {
        situation: null,
        task: null,
        action: null,
        result: null
      };
      this.elementHistory = {
        situation: [],
        task: [],
        action: [],
        result: []
      };
      this.currentElement = "situation";
      this.proposedCapture = null;
      this.userInput = "";
      this.streaming = false;
      this.streamingText = "";
      this.streamingStatus = "";
      this.conversationId = null;
      this.aiDraftedStar = false;
      this.viewMode = "walkthrough";
      this.currentAssistantMessage = this.greetingForElement("situation");
    },

    // ═══════════════ Cosmos write happens here ═══════════════

    async commitFullStar() {
      if (this.committingElement) return;
      if (
        !this.elementsCaptured.situation &&
        !this.elementsCaptured.task &&
        !this.elementsCaptured.action &&
        !this.elementsCaptured.result
      ) {
        return; // nothing to save
      }
      this.committingElement = true;
      try {
        const draft = {
          workKey: this.currentAccomplishment.workKey,
          // PR-FIX-COACH-RESUME: echo the existing achievementId so the
          // backend UPDATES the saved WorkAchievement's BRAM fields in
          // place instead of appending a new one. Without this, every
          // approve produced a duplicate — the original parser-extracted
          // bullet AND a new STAR-filled achievement — and the AIDone
          // count would still show old-bullet entries as 0-of-4 fields.
          achievementId: this.currentAccomplishment.achievementId || "",
          situation: this.elementsCaptured.situation || "",
          task: this.elementsCaptured.task || "",
          action: this.elementsCaptured.action || "",
          result: this.elementsCaptured.result || "",
          sourceNarrative: [
            this.sourceNarratives.situation,
            this.sourceNarratives.task,
            this.sourceNarratives.action,
            this.sourceNarratives.result
          ]
            .filter(Boolean)
            .join(" | "),
          // Headline stays empty so the existing achievement's DisplayCustom
          // (the original parsed bullet text) is preserved — that's the
          // user's source-of-truth claim. Only fill a headline when we're
          // appending a brand-new achievement that has no parser-extracted
          // text to inherit.
          headline: this.currentAccomplishment.achievementId
            ? ""
            : this.currentAccomplishment.originalText,
          // PR-GEN-STAR: audit stamp — distinguishes an AI-drafted STAR from
          // one the user talked through with the coach. Stamped only; no
          // scoring effect (per product decision).
          source: this.aiDraftedStar ? "ai_drafted" : "user_authored"
        };
        const resp = await approveStar(draft);
        if (resp && resp.success) {
          if (resp.capState) {
            this.winsCaptured = resp.capState.starsApproved;
            this.capReached = resp.capState.capReached;
            this.unlocked = resp.capState.unlimited;
          }
          // PR-FIX-COACH-COUNTER: mark this accomplishment's completion on
          // the queue item so the tracker chip ("X of M done") updates live
          // and stays consistent with the BRAM-derived count elsewhere.
          if (this.currentAccomplishment) {
            const doneCount = this.elementOrder.filter(
              el => (this.elementsCaptured[el] || "").trim()
            ).length;
            this.$set(this.currentAccomplishment, "completeCount", doneCount);
            this.$set(
              this.currentAccomplishment,
              "isComplete",
              doneCount === 4
            );
          }
          this.celebrationMessage = "Win captured ✓";
          this.celebrationVisible = true;

          try {
            await this.loadWork();
          } catch (e) {
            /* non-fatal */
          }

          // Cap fork takes priority over advancing.
          if (this.capReached && !this.unlocked) {
            setTimeout(() => {
              this.viewMode = "capFork";
            }, 600);
            return;
          }
          this.advanceAccomplishment();
        } else if (resp && resp.capReached) {
          if (resp.capState) {
            this.winsCaptured = resp.capState.starsApproved;
            this.unlocked = resp.capState.unlimited;
          }
          this.capReached = true;
          this.viewMode = "capFork";
        } else {
          this.errorMessage =
            (resp && (resp.displayText || resp.msg)) ||
            "Could not save the win — try again";
          this.errorVisible = true;
        }
      } catch (e) {
        this.errorMessage = "Could not save the win — try again";
        this.errorVisible = true;
      } finally {
        this.committingElement = false;
      }
    },

    advanceAccomplishment() {
      // PR-COACH-RESUME-FIX: advance to the next accomplishment that still
      // needs a STAR rather than blindly +1. The just-saved one is now
      // complete, and after a reload the queue may have been rebuilt, so a
      // positional +1 could skip a gap or land on an already-finished win.
      // If none remain incomplete, the walkthrough is done.
      var nextIdx = (this.accomplishments || []).findIndex(function(a) {
        return a && !a.isComplete;
      });
      if (nextIdx >= 0) {
        this.currentAccomplishmentIndex = nextIdx;
        this.startCurrentAccomplishment();
      } else {
        // All accomplishments captured. Route to /AIDone.
        this.$router.push({ path: "/AIDone" });
      }
    },

    // ═══════════════ Status + navigation ═══════════════

    async refreshStatus() {
      try {
        const resp = await getOnboardingStatus();
        if (resp && resp.success && resp.capState) {
          this.starCap = resp.capState.starCap || 3;
          this.winsCaptured = resp.capState.starsApproved || 0;
          this.unlocked = !!resp.capState.unlimited;
          this.capReached = !!resp.capState.capReached;
          this.isPremium = !!resp.isPremium;
          this.companySageUnlocked = !!resp.companySageUnlocked;
          // PR-GEN-STAR: load whether the user already acknowledged the
          // Generate-STAR disclosure so we know on mount whether to show it.
          this.aiDraftDisclosureAcknowledged =
            !!resp.aiStarDraftDisclosureAcknowledged;
        }
        this.statusLoaded = true;
      } catch (e) {
        this.statusLoaded = true;
      }
    },

    // ═══════════════ Generate STAR with AI (PR-GEN-STAR) ═══════════════

    // Entry point from the "Generate STAR with AI" button. First-time users
    // see the accuracy disclosure modal; after they've acknowledged it once
    // (persisted server-side), the button generates directly.
    onGenerateStarClick() {
      if (this.aiDrafting || this.streaming) return;
      // PR-COACH-STAR-UX (item 1): Generate used to silently discard whatever
      // the user had typed in the input box for the current element — it
      // regenerated from the resume bullet, filled only empty slots, and
      // jumped to review. The typed text vanished, which read as losing your
      // place. Fold any non-empty input into the CURRENT element first (only
      // if that slot is still empty, preserving the "never clobber captured"
      // rule) so Generate then drafts only the remaining empty slots around
      // what the user kept. Position (currentAccomplishmentIndex /
      // currentElement) is never touched here.
      this.preserveTypedInputBeforeGenerate();
      if (this.aiDraftDisclosureAcknowledged) {
        this.runGenerateStar();
      } else {
        this.aiDraftDisclosureDismissForever = false;
        this.showAiDraftDisclosure = true;
      }
    },

    // PR-COACH-STAR-UX (item 1): capture the current input box into the
    // current element so clicking Generate never throws away what the user
    // typed. Only writes when the input is non-empty AND the current element
    // slot is still empty — same "only fill EMPTY slots, never clobber a
    // captured element" rule runGenerateStar's merge follows. The text is the
    // user's own words, so no rephrase is needed before storing it; they
    // review every field on the next screen anyway. Returns nothing; clears
    // the input box once it's been folded in.
    preserveTypedInputBeforeGenerate() {
      const typed = (this.userInput || "").trim();
      if (!typed) return;
      const el = this.currentElement;
      if (el && !this.elementsCaptured[el]) {
        this.$set(this.elementsCaptured, el, typed);
        this.$set(this.sourceNarratives, el, typed);
      }
      this.userInput = "";
    },

    // Disclosure-modal confirm. If the user ticked "don't show again",
    // persist that server-side so it never re-fires on any device.
    async confirmDisclosureAndGenerate() {
      this.showAiDraftDisclosure = false;
      if (this.aiDraftDisclosureDismissForever) {
        this.aiDraftDisclosureAcknowledged = true;
        try {
          await acknowledgeAiStarDisclosure();
        } catch (e) {
          /* non-fatal — they just see the modal again next session */
        }
      }
      this.runGenerateStar();
    },

    // Calls the one-shot AI draft endpoint, then merges the result into the
    // four STAR slots. PR-GEN-STAR rule: only fill EMPTY slots — never
    // clobber an element the user already captured by hand.
    //
    // PR-COACH-STAR-UX (item 1): this NEVER resets the accomplishment position
    // (currentAccomplishmentIndex / currentElement). It only fills empty slots
    // and moves to review — it must not feel like a restart. Any text the user
    // had typed for the current element is already folded into elementsCaptured
    // by preserveTypedInputBeforeGenerate (called from onGenerateStarClick), so
    // the merge below skips that now-filled slot and nothing is lost.
    async runGenerateStar() {
      const accomp = this.currentAccomplishment;
      if (!accomp || this.aiDrafting) return;
      this.aiDrafting = true;
      try {
        const resp = await generateStarDraft({
          bulletText: accomp.originalText || "",
          roleTitle: this.firstRoleTitle(accomp),
          company: this.firstRoleCompany(accomp),
          dateRange: this.firstRoleDateRange(accomp)
        });
        if (!resp || !resp.success || !resp.data) {
          this.errorMessage =
            (resp && (resp.displayText || resp.msg)) ||
            "Couldn't generate a draft — try the step-by-step coach.";
          this.errorVisible = true;
          return;
        }
        const d = resp.data;
        // Merge: fill only the slots the user hasn't already captured.
        const merge = (el, val) => {
          if (!this.elementsCaptured[el] && val) {
            this.$set(this.elementsCaptured, el, val);
          }
        };
        merge("situation", d.situation);
        merge("task", d.task);
        merge("action", d.action);
        merge("result", d.result);
        this.aiDraftedStar = true;
        // Server may report the disclosure as already acknowledged.
        if (d.disclosureAcknowledged) {
          this.aiDraftDisclosureAcknowledged = true;
        }
        this.viewMode = "review";
      } catch (e) {
        this.errorMessage =
          "Couldn't generate a draft — try the step-by-step coach.";
        this.errorVisible = true;
      } finally {
        this.aiDrafting = false;
      }
    },

    // Helpers: pull role title / company / date-range from the queue item's
    // display fields (fullList exposes pre-rendered strings, not raw objects).
    firstRoleTitle(accomp) {
      if (!accomp || !accomp.roleContextLine) return "";
      return (accomp.roleContextLine.split(" · ")[0] || "").trim();
    },
    firstRoleCompany(accomp) {
      if (!accomp || !accomp.roleContextLine) return "";
      const parts = accomp.roleContextLine.split(" · ");
      return (parts[1] || "").trim();
    },
    firstRoleDateRange(accomp) {
      if (!accomp || !accomp.roleContextLine) return "";
      const parts = accomp.roleContextLine.split(" · ");
      return (parts[2] || "").trim();
    },

    switchToStepByStep() {
      // PR-FIX-COACH-HANDOFF: carry mode=step so AIWorkAchievements skips
      // the Sage-vs-step mode-choice card (the user already chose), and
      // workKey so it resumes on the SAME role — not fullList[0]. Both the
      // Coach and step-by-step read/write the same `work` store + Cosmos,
      // so nothing the user already captured is lost in the handoff.
      //
      // PR-COACH-POINTER: also persist the achievementId in Vuex so
      // step-by-step lands on the EXACT achievement we were on, not just
      // the first one in this work entry. The query param carries the
      // pointer too as a belt-and-suspenders for direct URL navigation.
      this.savePointerFromCurrent();
      const query = { mode: "step" };
      if (this.currentAccomplishment) {
        query.workKey = this.currentAccomplishment.workKey;
        if (this.currentAccomplishment.achievementId) {
          query.achievementId = this.currentAccomplishment.achievementId;
        }
      }
      this.$router.push({ path: "/AIWorkAchievements", query });
    },
    async saveAndExit() {
      // PR-COACH-POINTER: preserve position on exit-to-Done. If the user
      // comes back to the agent flow later (e.g., via direct nav), they
      // resume at the same accomplishment. The pointer is cleared in
      // AIDone.mounted when the flow is truly complete.
      this.savePointerFromCurrent();
      this.$router.push({ path: "/AIDone" });
    },
    upgradeToPlus() {
      // PR-COACH-CAP-FORK: stash where we want the user to land after a
      // successful Stripe checkout. ResumeLanding (where Stripe's
      // success_url drops the user with ?session_id=...) reads this on
      // mount and redirects accordingly. Without this, the user upgrades
      // mid-coach and gets dumped on the generic landing page — momentum
      // lost. TTL is 30 min so a forgotten checkout from yesterday doesn't
      // teleport tomorrow's user.
      //
      // PR-COACH-POINTER: also persist the pointer to Vuex so when they
      // return (success OR cancel from Stripe), Coach.mounted seeks them
      // back to the same accomplishment. Vuex is sessionStorage-backed via
      // vuex-persistedstate so it survives the Stripe roundtrip.
      this.savePointerFromCurrent();
      try {
        sessionStorage.setItem(
          "paymentReturn",
          JSON.stringify({
            to: "/AIWorkAchievementsCoach",
            reason: "coach_cap_upgrade",
            ts: Date.now()
          })
        );
      } catch (e) {
        /* sessionStorage unavailable — non-fatal, user just lands on
           the default post-payment page instead of returning to Coach */
      }
      // PR-COACH-CAP-FORK: was pushing to "/Plans" — a route that doesn't
      // exist in router/index.js. Vue Router silently had no match, so
      // the button effectively did nothing. The actual upgrade page is
      // /Upgrade.
      this.$router.push({ path: "/Upgrade" });
    },
    goAddWork() {
      this.$router.push({ path: "/AIWork" });
    },

    // ═══════════════ Small helpers ═══════════════

    chipLabel(el) {
      return el.charAt(0).toUpperCase() + el.slice(1);
    },
    chipIcon(el) {
      if (this.elementsCaptured[el]) return "mdi-check-circle";
      if (this.currentElement === el) return "mdi-record-circle";
      return "mdi-checkbox-blank-circle-outline";
    },
    renderMessage(text) {
      if (!text) return "";
      // Bold **bold** for clarity on element names in seed greetings
      const escaped = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return escaped
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    }
  }
};
</script>

<style scoped>
.tracker-bar {
  background: #fffcf2;
  border: 1px solid #ffe9a8;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  position: sticky;
  top: 0;
  z-index: 5;
}
.review-progress-line {
  color: #2e7d32;
}
.theme--dark .review-progress-line {
  color: #81c784;
}

/* PR-COACH-DARK-AWARE: the Coach's capture cards are warm/light in LIGHT mode.
   In light mode we pin readable dark text on them (the light surfaces would
   otherwise inherit a slightly different theme color). In DARK mode the cards
   switch to a dark surface (rules further below) with light text, so we scope
   these dark-text pins to the light theme only and let the dark-theme rules
   take over otherwise. This replaces the prior approach that forced the cards
   light in BOTH themes — which clashed badly with the dark app shell. */
.theme--light .tracker-bar,
.theme--light .accomplishment-ref,
.theme--light .captured-so-far,
.theme--light .capture-card,
.theme--light .warning-banner,
.theme--light .milestone-bubble {
  color: #2b2f3a;
}
.theme--light .tracker-bar .text--primary,
.theme--light .accomplishment-ref .text--primary,
.theme--light .captured-so-far .text--primary,
.theme--light .capture-card .text--primary,
.theme--light .milestone-bubble .text--primary {
  color: #2b2f3a !important;
}
.theme--light .tracker-bar .text--secondary,
.theme--light .accomplishment-ref .text--secondary,
.theme--light .captured-so-far .text--secondary,
.theme--light .capture-card .text--secondary,
.theme--light .warning-banner .text--secondary,
.theme--light .milestone-bubble .text--secondary {
  color: #5b6270 !important;
}
.theme--light .capture-card >>> .v-textarea textarea {
  color: #2b2f3a !important;
}

/* PR-COACH-DARK-AWARE: dark-mode surfaces. Each card gets a dark surface with
   a tasteful accent tint instead of the light pastel, and all text (primary +
   secondary, plus the textarea on the capture card) is pinned to a readable
   light color so nothing reads as dark-on-dark. */
.theme--dark .tracker-bar {
  background: #2c2718;
  border-color: #5a4a1f;
  color: #ececec;
}
.theme--dark .accomplishment-ref {
  background: #1e2530;
  border-left-color: #64b5f6;
  color: #ececec;
}
.theme--dark .captured-so-far {
  background: #16202c;
  border-color: #2c4660;
  color: #ececec;
}
.theme--dark .capture-card {
  background: #16202c;
  border-color: #2c4660;
  color: #ececec;
}
.theme--dark .warning-banner {
  background: #332b14;
  border-color: #5a4a1f;
  color: #ececec;
}
.theme--dark .milestone-bubble {
  background: #332b14;
  border-color: #5a4a1f;
  color: #ececec;
}
.theme--dark .tracker-bar .text--primary,
.theme--dark .accomplishment-ref .text--primary,
.theme--dark .captured-so-far .text--primary,
.theme--dark .capture-card .text--primary,
.theme--dark .milestone-bubble .text--primary {
  color: #f1f1f1 !important;
}
.theme--dark .tracker-bar .text--secondary,
.theme--dark .accomplishment-ref .text--secondary,
.theme--dark .captured-so-far .text--secondary,
.theme--dark .capture-card .text--secondary,
.theme--dark .warning-banner .text--secondary,
.theme--dark .milestone-bubble .text--secondary {
  color: #b6bcc8 !important;
}
/* The capture-card textarea is theme-colored; in dark mode keep it light so
   the editable captured value stays readable on the dark surface. */
.theme--dark .capture-card >>> .v-textarea textarea {
  color: #f1f1f1 !important;
}

.star-chip {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
  transition: all 150ms ease;
}
.star-chip-pending {
  background: #f5f5f5;
  color: #9e9e9e;
}
.star-chip-current {
  background: #e3f2fd;
  color: #1565c0;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}
.star-chip-done {
  background: #e8f5e9;
  color: #2e7d32;
}
.accomplishment-ref {
  background: #fafafa;
  border-left: 4px solid #1976d2;
}
/* PR-COACH-CONTEXT: role · company · date range subtitle. Same caption
   color as the surrounding "From your resume" text, slightly heavier
   weight on the title to give it a touch more presence without
   dominating the bullet itself. */
.role-context-line {
  font-weight: 500;
  color: #455a64;
}
.theme--dark .role-context-line {
  color: #b0bec5;
}
.captured-so-far {
  background: #f5f9ff;
  border: 1px solid #bbdefb;
  border-radius: 8px;
}
/* PR-COACH-STAR-UX (item 2): the captured-so-far block is now a collapsed
   expansion panel. Strip Vuetify's default panel chrome (it ships its own
   background + min-height) so it reads as the same tidy card it did before,
   and tighten the content padding so the prose doesn't gain extra inset. */
.captured-so-far-panels,
.captured-so-far.v-expansion-panel,
.captured-so-far.v-expansion-panel::before {
  box-shadow: none;
  background: transparent;
}
.captured-so-far >>> .v-expansion-panel-header {
  min-height: 40px;
  padding: 6px 12px;
}
.captured-so-far >>> .v-expansion-panel-content__wrap {
  padding: 0 12px 8px;
}
/* PR-COACH-DARK-AWARE: the panel sits on the captured-so-far card. In light
   mode pin readable dark text; in dark mode the card is now a dark surface, so
   pin readable light text instead. */
.theme--light .captured-so-far >>> .v-expansion-panel-header,
.theme--light .captured-so-far >>> .v-expansion-panel-header .v-icon,
.theme--light .captured-so-far >>> .v-expansion-panel-content {
  color: #2b2f3a;
}
.theme--dark .captured-so-far >>> .v-expansion-panel-header,
.theme--dark .captured-so-far >>> .v-expansion-panel-header .v-icon,
.theme--dark .captured-so-far >>> .v-expansion-panel-content {
  color: #f1f1f1;
}
.captured-element-row {
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.captured-element-row:last-child {
  border-bottom: none;
}
.capture-card {
  background: #f5f9ff;
  border: 1px solid #bbdefb;
  border-radius: 12px;
}
.warning-banner {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
}
.thinking-dots .dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #90a4ae;
  margin-right: 4px;
  animation: dot-pulse 1.2s ease-in-out infinite;
}
.thinking-dots .dot:nth-child(2) {
  animation-delay: 0.15s;
}
.thinking-dots .dot:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes dot-pulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.85);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}
.milestone-bubble {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 16px;
}
</style>
